import React, { useEffect, useMemo, useState } from "react";
import { Tab, Tabs } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useAuthorization } from "../../components/Authorization/useAuthorization.js";
import General from "./General/General.jsx";
import { Back, Next, Save } from "../../components/Icons/Icons.jsx";
import ISFFiles from "./ISFFiles/ISFFiles.jsx";
import { useClinicalTrialContext } from "./ClinicalTrialContext.jsx";
import TrialStatusChip from "../../components/CustomControls/TrialStatusChip.jsx";
import { Spinner } from "../../components/Utils/Loaders.jsx";
import {
  saveEditedCategories,
  updateClinicalTrial,
  updateNotificationsConfig,
  updateAttachmentPathsSubtitlesBatch,
} from "../../api/clinicalTrial.js";
import { HandleApiError } from "../../components/Notifications/APIErrorHandler.js";
import RegulatoryContainer from "./Regulatory/RegulatoryContainer.jsx";
import { NotificationEvents } from "./Notifications/NotificationEvents.jsx";
import { AuditModal } from "../../components/AuditTrail/AuditModal.jsx";
import { useAttachmentSlotPolling } from "../../components/CustomControls/DocumentUpload/hooks/AttachmentSlotPollingProvider.jsx";
import { AuditTrail } from "../Admin/AuditTrail.jsx";
import { toast } from "react-toastify";
import StartupDevices from "./Devices/StartupDevices.jsx";
import moment from "moment";

const ClinicalTrialContainer = ({ history, location, ...props }) => {
  const { t } = useTranslation();
  const [, user, isAuthorized] = useAuthorization();
  const { startup, setStartup } = useClinicalTrialContext();
  const [saving, setSaving] = useState(false);
  const [savingIsf, setSavingIsf] = useState(false);
  const [showActivateCenterPanel, setShowActivateCenterPanel] = useState(true);
  const [activeTab, setActiveTab] = useState("General");
  const [notificationsConfig, setNotificationsConfig] = useState({});
  const [modifiedCategories, setModifiedCategories] = useState([]);
  const [modifiedSubtitles, setModifiedSubtitles] = useState({});
  const [isfFilesRefreshKey, setIsfFilesRefreshKey] = useState(0);
  const [showAuditModal, setShowAuditModal] = useState(false);

  const polling = useAttachmentSlotPolling();

  const setCurrentUserFromAuth = polling?.setCurrentUserFromAuth;
  const setUsersFromProtocolPractitioners =
    polling?.setUsersFromProtocolPractitioners;

  useEffect(() => {
    if (!setCurrentUserFromAuth) return;
    setCurrentUserFromAuth(user);
  }, [setCurrentUserFromAuth, user]);

  useEffect(() => {
    const protocolPractitioners =
      startup?.protocol?.protocolPractitioners || [];

    if (!setUsersFromProtocolPractitioners) return;

    setUsersFromProtocolPractitioners(protocolPractitioners);
  }, [setUsersFromProtocolPractitioners, startup]);

  const isProtocolActivated = useMemo(() => {
    if (!startup?.protocol?.status?.code) {
      return false;
    }

    switch (startup?.protocol?.status?.code) {
      case "Recruiting":
      case "OnGoing":
      case "OnHold":
        return true;
      default:
        return false;
    }
  }, [startup]);

  const showRegulatoryAndProtocolTabs = useMemo(() => {
    const code =
      startup?.protocol?.status?.code?.key ??
      startup?.protocol?.status?.code;
    return (
      code === "Recruiting" ||
      code === "OnGoing" ||
      code === "OnHold" ||
      code === "Closed" ||
      code === "StartUp"
    );
  }, [startup]);

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (!p.get("tab")) {
      p.set("tab", "General");
      history.replace({ pathname: location.pathname, search: p.toString() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTabFromLocation = () => {
    const p = new URLSearchParams(location.search);
    return p.get("tab") || "General";
  };

  // sincronizo el tab con la URL
  useEffect(() => {
    const urlTab = getTabFromLocation();
    if (urlTab !== activeTab) {
      setActiveTab(urlTab);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleSave = async (newStatus, callback) => {
    const newStartup = { ...startup };
    newStartup.attachments = newStartup.attachments.filter((x) => !!x.id);

    // Formatear preselectionDate a solo fecha (YYYY-MM-DD) sin hora
    if (newStartup.preselectionDate) {
      if (moment.isMoment(newStartup.preselectionDate)) {
        newStartup.preselectionDate =
          newStartup.preselectionDate.format("YYYY-MM-DD");
      } else if (newStartup.preselectionDate instanceof Date) {
        newStartup.preselectionDate = moment(
          newStartup.preselectionDate
        ).format("YYYY-MM-DD");
      } else if (typeof newStartup.preselectionDate === "string") {
        // Si ya es un string, intentar parsearlo y formatearlo
        const parsed = moment(newStartup.preselectionDate);
        if (parsed.isValid()) {
          newStartup.preselectionDate = parsed.format("YYYY-MM-DD");
        }
      }
    }

    if (newStatus && newStartup.protocol?.status) {
      newStartup.protocol.status.code = newStatus;
    }

    setSaving(true);
    try {
      var result = await updateClinicalTrial(newStartup);
      if (result.status === 200) {
        if (result.data.warnings?.length) {
          result.data.warnings.forEach((warning) => {
            toast.warning(t(warning));
          });
        } else {
          toast.success(t("clinicalTrials.general.formSaved"));
        }
      }

      if (!!newStatus) {
        setStartup((prev) => ({
          ...prev,
          protocol: {
            ...prev.protocol,
            status: {
              ...prev.protocol.status,
              code: newStatus,
            },
          },
        }));
        if (callback) {
          callback();
        }
      }
    } catch (err) {
      HandleApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotifications = async () => {
    const result = [];

    Object.entries(notificationsConfig).forEach(([eventId, practitioners]) => {
      Object.entries(practitioners).forEach(([practitionerId, channels]) => {
        result.push({
          startupId: startup.id,
          eventId: parseInt(eventId),
          practitionerId,
          email: channels.email ? 1 : 0,
          sms: channels.sms ? 1 : 0,
          whatsapp: channels.whatsapp ? 1 : 0,
        });
      });
    });

    try {
      const response = await updateNotificationsConfig(result);
      if (response?.status === 200) {
        toast.success(t("clinicalTrials.general.formSaved"));
      }
    } catch (err) {
      HandleApiError(err);
    }
  };

  const handleSaveISFFiles = async () => {
    try {
      setSavingIsf(true);
      await saveEditedCategories(modifiedCategories);
      const subtitlesArray = Object.entries(modifiedSubtitles).map(
        ([path, subtitle]) => ({
          path,
          subtitle: subtitle || "",
        })
      );
      if (subtitlesArray.length > 0) {
        await updateAttachmentPathsSubtitlesBatch(startup.id, subtitlesArray);
      }
      toast.success(t("clinicalTrials.general.formSaved"));
      setModifiedSubtitles({});
      setIsfFilesRefreshKey((k) => k + 1);
    } catch (err) {
      HandleApiError(err);
    } finally {
      setSavingIsf(false);
    }
  };

  const handleChangeStatus = async (newStatus) => {
    if (!newStatus) return;

    setStartup((prev) => ({
      ...prev,
      protocol: {
        ...prev.protocol,
        status: { ...(prev.protocol?.status || {}), code: newStatus },
      },
      statusHistory: [
        ...(prev.statusHistory || []),
        { status: newStatus, date: new Date().toISOString() },
      ],
    }));
    await handleSave(newStatus);
  };

  if (!startup || !!saving) {
    return <Spinner />;
  }

  const handleAuditTrail = () => {
    setShowAuditModal(true);
  };

  return (
    <>
      {(() => {
        switch (activeTab) {
          case "notifications":
            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: "12px",
                }}
              >
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() => handleSaveNotifications()}
                >
                  <Save />
                  {t("clinicalTrials.notifications.save")}
                </button>
              </div>
            );

          case "isf-files":
            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: "12px",
                }}
              >
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={() => handleSaveISFFiles()}
                  disabled={savingIsf}
                >
                  <Save />
                  {t("clinicalTrials.notifications.save")}
                </button>
              </div>
            );

          default:
            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: "12px",
                }}
              >
                <TrialStatusChip
                  status={
                    startup?.protocol?.status?.code?.key ??
                    startup?.protocol?.status?.code
                  }
                />
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={handleAuditTrail}
                >
                  {t("protocols.visitTracking.auditTrail")}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleSave()}
                >
                  <Save />
                  {t("clinicalTrials.general.save")}
                </button>
              </div>
            );
        }
      })()}

      <div style={{ position: "relative" }}>
        <div
          style={{ position: "absolute", right: 0, top: 0, marginTop: "5px" }}
        >
          {isProtocolActivated &&
            showActivateCenterPanel &&
            activeTab !== "notifications" && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setShowActivateCenterPanel(!showActivateCenterPanel);
                }}
              >
                <Back />
              </button>
            )}

          {isProtocolActivated &&
            !showActivateCenterPanel &&
            activeTab !== "notifications" && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setShowActivateCenterPanel(!showActivateCenterPanel);
                }}
              >
                <Next />
              </button>
            )}
        </div>

        <Tabs
          activeKey={activeTab}
          id="general-id"
          mountOnEnter={true}
          onSelect={(key) => {
            setActiveTab(key);

            const p = new URLSearchParams(location.search);
            p.set("tab", key);
            history.push({ pathname: location.pathname, search: p.toString() });

            if (key === "protocol") {
              history.push(`/admin/protocols/${startup?.protocol.id}`);
            }
          }}
        >
          <Tab
            eventKey={"General"}
            title={t("clinicalTrials.header.tabGeneral")}
          >
            <General
              onSaveStartup={handleSave}
              onChangeStatus={handleChangeStatus}
              expandedAll={true}
              showActivateCenterPanel={showActivateCenterPanel}
              setShowActivateCenterPanel={setShowActivateCenterPanel}
              isProtocolActivated={isProtocolActivated}
            />
          </Tab>
          <Tab eventKey={"isf-files"} title={t("clinicalTrials.header.tabIsf")}>
            <ISFFiles
              {...props}
              expandedAll={true}
              setModifiedCategories={setModifiedCategories}
              setModifiedSubtitles={setModifiedSubtitles}
              modifiedSubtitles={modifiedSubtitles}
              startup={startup}
              refreshKey={isfFilesRefreshKey}
            />
          </Tab>

          {showRegulatoryAndProtocolTabs && (
            <Tab
              eventKey={"regulatory"}
              title={t("clinicalTrials.header.tabRegulatory")}
            >
              <RegulatoryContainer {...props} expandedAll={true} />
            </Tab>
          )}
          {isAuthorized(["Protocols.View"]) && showRegulatoryAndProtocolTabs && (
            <Tab
              eventKey={"protocol"}
              title={t("clinicalTrials.header.tabProtocol")}
            />
          )}

          <Tab
            eventKey={"notifications"}
            title={t("clinicalTrials.header.tabNotification")}
          >
            <NotificationEvents
              {...props}
              startup={startup}
              expandedAll={true}
              onStateChange={(config) => setNotificationsConfig(config)}
            />
          </Tab>

          {isAuthorized(["Startups.Edit"]) && (
            <Tab
              eventKey={"startup-devices"}
              title={t("clinicalTrials.header.startupDevices")}
            >
              <StartupDevices startup={startup} />
            </Tab>
          )}
        </Tabs>
      </div>
      {showAuditModal && (
        <AuditModal
          onClose={() => setShowAuditModal(false)}
          title={t("auditTrail.audit")}
        >
          <AuditTrail entity="Startup" entityId={startup.id} />
        </AuditModal>
      )}
    </>
  );
};

export default ClinicalTrialContainer;
